import { Controller } from '@hotwired/stimulus';
import { parseFormattedNumber } from '../utils/parse_formatted_number';

const FORMAT_PARTS_SAMPLE = 1234567.89;

export default class NumberFormatController extends Controller {
  static targets = ['input'];
  static values = {
    locale: { type: String, default: 'hu-HU' },
    scale: { type: Number, default: 0 },
  };

  connect() {
    this.formatter = new Intl.NumberFormat(this.localeValue, {
      useGrouping: true,
      minimumFractionDigits: 0,
      maximumFractionDigits: this.scaleValue,
    });

    this.decimalSeparator = this._detectDecimalSeparator();

    this._seedRawValue();
  }

  onKeydown(e) {
    if (this._handleInvalidKey(e)) {
      return;
    }

    if (this._handleDecimalSeparatorKey(e)) {
      return;
    }

    this._handleBackspace(e);
  }

  _detectDecimalSeparator() {
    if (this.scaleValue === 0) {
      return null;
    }

    const parts = this.formatter.formatToParts(FORMAT_PARTS_SAMPLE);
    return parts.find((p) => p.type === 'decimal')?.value ?? null;
  }

  _seedRawValue() {
    if (this.inputTarget.value) {
      const raw = parseFormattedNumber(this.inputTarget.value, this.decimalSeparator);
      this.inputTarget.dataset.rawValue = raw ?? '';
    }
  }

  _handleDecimalSeparatorKey(e) {
    const isDecimalSeparatorKey = this.decimalSeparator && e.key === this.decimalSeparator;

    if (!isDecimalSeparatorKey) {
      return false;
    }

    if (this.inputTarget.value.includes(this.decimalSeparator)) {
      e.preventDefault();
    }

    return true;
  }

  _handleBackspace(e) {
    if (e.key !== 'Backspace') {
      return;
    }

    const input = this.inputTarget;
    const pos = input.selectionStart;

    // pos < 2: need at least one digit + one separator before the cursor
    if (pos !== input.selectionEnd || pos < 2) {
      return;
    }

    const charBefore = input.value[pos - 1];
    if (/\d/.test(charBefore) || charBefore === this.decimalSeparator) {
      return;
    }

    e.preventDefault();
    this._deleteDigitBeforeSeparator(input, pos);
  }

  _handleInvalidKey(e) {
    if (!e.key) {
      return false;
    }

    const hasModifierKey = e.ctrlKey || e.metaKey;
    const isInvalidChar = e.key.length === 1 && !/\d/.test(e.key) && e.key !== this.decimalSeparator;

    if (!hasModifierKey && isInvalidChar) {
      e.preventDefault();
      return true;
    }

    return false;
  }

  _parseOrReset(input) {
    const parsed = parseFormattedNumber(input.value, this.decimalSeparator);
    if (parsed === null) {
      input.value = '';
      input.dataset.rawValue = '';
    }
    return parsed;
  }

  _applyFormat(input, rawValue) {
    input.value = this.formatter.format(Number(rawValue));
    input.dataset.rawValue = rawValue;
  }

  _deleteDigitBeforeSeparator(input, pos) {
    input.value = input.value.slice(0, pos - 2) + input.value.slice(pos - 1);
    input.setSelectionRange(pos - 2, pos - 2);
    this.onInput();
  }

  onInput() {
    const input = this.inputTarget;
    const displayValue = input.value;
    const cursorPos = input.selectionStart;

    if (this.decimalSeparator && displayValue.endsWith(this.decimalSeparator)) {
      return;
    }

    const rawValue = this._parseOrReset(input);
    if (rawValue === null) {
      return;
    }

    // Preserve intermediate fractional state: "0,0" parses as 0 but must not collapse to "0"
    if (rawValue === 0 && displayValue !== '0') {
      input.dataset.rawValue = rawValue;
      return;
    }

    const displayLength = displayValue.length;
    this._applyFormat(input, rawValue);

    const diff = input.value.length - displayLength;
    const newPos = Math.max(0, cursorPos + diff);

    input.setSelectionRange(newPos, newPos);
  }

  onBlur() {
    const input = this.inputTarget;
    const rawValue = this._parseOrReset(input);
    if (rawValue !== null) {
      this._applyFormat(input, rawValue);
    }

    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
